import { TaxonomyField, type ImageArrayObject } from "types/ContentHub/Common";
import { generateShimmerBlur } from "./imageUtils";
import {
  CoveoCard,
  CoveoConfigOptions,
  CoveoContextTaxonomy,
  CoveoSearchApiQuery,
  CoveoStaticFetchParams,
  CustomEnumOption,
  TaxonomyContext,
} from "../types/Coveo";
import { CoveoFieldName } from "./coveoUtils";
import { CoveoSearchRequest } from "lib/search/CoveoSearchApi";
import { TaxonomyFields } from "../types/ContentHub/Taxonomy";
import { TaxonomyItem } from "../types/Search/TaxonomyItem";
import fallbackImage from "src/assets/images/fallback-image.jpg";
import { DEFAULT_LOCALE } from "src/i18n/config";
import { capitalizeLanguageRegion } from "./localeUtils";

type CoveoField = (typeof CoveoFieldName)[keyof typeof CoveoFieldName];

type TaxonomyContextResolver = {
  field: CoveoField;
  getLabels: (
    ctx: TaxonomyFields,
    locale?: string,
  ) => ReadonlyArray<string | null | undefined>;
};

const defaultLocale = capitalizeLanguageRegion(DEFAULT_LOCALE) || "en-US";

const labelsFromTaxonomyField = (
  field: TaxonomyField | undefined,
  locale: string = defaultLocale,
): ReadonlyArray<string | null | undefined> =>
  field?.results?.map((r) => r.Label[locale]) ?? [];

const TAXONOMY_CONTEXT_RESOLVERS: Partial<
  Record<TaxonomyContext, TaxonomyContextResolver>
> = {
  [TaxonomyContext.Applications]: {
    field: CoveoFieldName.TaxonomyApplications,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.Application, locale),
  },
  [TaxonomyContext.Solutions]: {
    field: CoveoFieldName.TaxonomySolutions,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.Solution, locale),
  },
  [TaxonomyContext.Industries]: {
    field: CoveoFieldName.TaxonomyIndustries,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.Industries, locale),
  },
  [TaxonomyContext.Markets]: {
    field: CoveoFieldName.TaxonomyMarkets,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.Market, locale),
  },
  [TaxonomyContext.Regions]: {
    field: CoveoFieldName.TaxonomyCountries,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.Countries, locale),
  },
  [TaxonomyContext.Countries]: {
    field: CoveoFieldName.TaxonomyCountries,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.Countries, locale),
  },
  [TaxonomyContext.Companies]: {
    field: CoveoFieldName.TaxonomyCompanies,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.Companies, locale),
  },
  [TaxonomyContext.ArticleTopic]: {
    field: CoveoFieldName.TaxonomyArticleTopics,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.ArticleTopic, locale),
  },
  [TaxonomyContext.ArticleType]: {
    field: CoveoFieldName.TaxonomyArticleTypes,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.ArticleType, locale),
  },
  [TaxonomyContext.EventsType]: {
    field: CoveoFieldName.EventType,
    getLabels: (ctx) => (ctx.EventType ? [ctx.EventType] : []),
  },
  [TaxonomyContext.NewsTopic]: {
    field: CoveoFieldName.TaxonomyNewsTopics,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.NewsTopics, locale),
  },
  [TaxonomyContext.NewsType]: {
    field: CoveoFieldName.TaxonomyNewsTypes,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.NewsTypes, locale),
  },
  [TaxonomyContext.StoryTopic]: {
    field: CoveoFieldName.TaxonomyStoryTopics,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.StoryTopic, locale),
  },
  [TaxonomyContext.StoryType]: {
    field: CoveoFieldName.TaxonomyStoryTypes,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.StoryType, locale),
  },
  [TaxonomyContext.GA4RepGroup]: {
    field: CoveoFieldName.TaxonomyGoogleAnalyticsRepGroup,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.GoogleAnalyticsRepGroup, locale),
  },
  [TaxonomyContext.TranslationOwner]: {
    field: CoveoFieldName.TaxonomyTranslationOwner,
    getLabels: (ctx, locale = defaultLocale) =>
      labelsFromTaxonomyField(ctx.TranslationOwner, locale),
  },
};

const taxonomyLabelValue = (
  item: TaxonomyItem | undefined,
): string | undefined => item?.fields?.TaxonomyLabel?.value;

const enumOptionValue = (
  item: CustomEnumOption | undefined,
): string | undefined => item?.fields?.Value?.value;

const buildInClause = <T>(
  items: ReadonlyArray<T> | undefined,
  field: CoveoField,
  getValue: (item: T) => string | undefined,
): string | undefined => {
  if (!items?.length) return undefined;

  const values = items
    .map(getValue)
    .filter((v): v is string => !!v)
    .map((v) => `"${v}"`);

  return values.length > 0 ? `${field}==(${values.join(", ")})` : undefined;
};

const buildTaxonomyContextQuery = (
  taxonomyContext: TaxonomyFields,
  contextTaxonomies: ReadonlyArray<CoveoContextTaxonomy>,
  locale: string = defaultLocale,
): string | undefined => {
  const clauses: string[] = [];

  for (const taxonomy of contextTaxonomies) {
    const resolver =
      TAXONOMY_CONTEXT_RESOLVERS[
        taxonomy.fields.Value.value as TaxonomyContext
      ];
    if (!resolver) continue;

    const values = resolver
      .getLabels(taxonomyContext, locale)
      .filter((label): label is string => !!label)
      .map((label) => `"${label}"`);

    if (values.length > 0) {
      clauses.push(`${resolver.field}==(${values.join(",")})`);
    }
  }

  return clauses.length > 0 ? clauses.join(" AND ") : undefined;
};

export const isExternalURL = (url: string): boolean => {
  const publicUrl = process.env.NEXT_PUBLIC_URL ?? "https://www.ecolab.com";
  try {
    const tempUrl = new URL(url, publicUrl);
    return tempUrl.origin !== publicUrl;
  } catch {
    return false;
  }
};

export const getLinkTargetValue = (url: string): "_blank" | "" =>
  isExternalURL(url) ? "_blank" : "";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const monthName = (month: number | string): string => {
  const idx = (typeof month === "number" ? month : Number(month)) - 1;
  if (idx < 0 || idx > 11) throw new Error(`Invalid month: ${month}`);
  return MONTHS[idx];
};

const to12HourString = (h24: number): string => {
  let h = h24;
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return String(h).padStart(2, "0");
};

const ampm = (hour24: number): "AM" | "PM" => (hour24 >= 12 ? "PM" : "AM");

/**
 * Formats Coveo date string "as-is" (NO timezone conversion).
 *
 * Supports inputs:
 *  1) "YYYY-MM-DD"
 *  2) "YYYY-MM-DDTHH:mm:ssZ" (also milliseconds or offsets like +05:30)
 *  3) Epoch timestamps in ms (e.g., "1569369600000") or as number
 *
 * Returns:
 *  - If time present: "NOVEMBER 16, 2026 | 10:00 AM"
 *  - Else:           "NOVEMBER 16, 2026"
 */
export function formatCoveoDateWithoutConversion(
  input: string | number,
  isDateOnly = false,
): string {
  if (input === null || input === undefined) return `${input}`;

  const s = String(input).trim();
  if (!s) return `${input}`;

  // Case 0: Epoch timestamp (milliseconds or seconds)
  if (/^\d{10,13}$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n)) return `${input}`;

    const ms = s.length === 10 ? n * 1000 : n;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return `${input}`;

    // Use UTC getters to avoid local timezone conversion.
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    const hour = d.getUTCHours();
    const minute = d.getUTCMinutes();

    const datePart = `${monthName(month)} ${day}, ${year}`;
    if (isDateOnly) return datePart;

    const timePart = `${to12HourString(hour)}:${String(minute).padStart(2, "0")} ${ampm(hour)}`;
    return `${datePart} | ${timePart.toUpperCase()}`;
  }

  // Case 1: Date-only "YYYY-MM-DD"
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return `${monthName(m)} ${Number(d)}, ${y}`;
  }

  // Case 2: Datetime ISO-like with time: "YYYY-MM-DDTHH:mm..."
  const DATE_PART = String.raw`(\d{4})-(\d{2})-(\d{2})`;
  const TIME_PART = String.raw`(\d{2}):(\d{2})`;
  const SECONDS_PART = String.raw`(?::\d{2}(?:\.\d{1,9})?)?`;

  const WITH_TZ = new RegExp(
    String.raw`^${DATE_PART}T${TIME_PART}${SECONDS_PART}(?:Z|[+-]\d{2}:?\d{2})?$`,
  );

  const WITHOUT_TZ = new RegExp(`^${DATE_PART}T${TIME_PART}${SECONDS_PART}$`);

  const dt = WITH_TZ.exec(s) || WITHOUT_TZ.exec(s);

  if (dt) {
    const [, y, m, d, HH, MM] = dt;
    const datePart = `${monthName(m)} ${Number(d)}, ${y}`;
    if (isDateOnly) return datePart;

    const hour24 = Number(HH);
    const timePart = `${to12HourString(hour24)}:${MM} ${ampm(hour24)}`;
    return `${datePart} | ${timePart.toUpperCase()}`;
  }

  return `${input}`;
}

export function parseCoveoDate(
  value: number | string | null | undefined,
): Date | null {
  if (value == null) return null;

  const createDate = (ms: number): Date | null => {
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const asNumber = typeof value === "number" ? value : Number(value);

  const isNumeric =
    Number.isFinite(asNumber) && /^-?\d+(\.\d+)?$/.test(String(value).trim());

  if (isNumeric) {
    const abs = Math.abs(asNumber);

    if (abs >= 1e18) return createDate(asNumber / 1e6);
    if (abs >= 1e15) return createDate(asNumber / 1e3); // 16-digit microseconds
    if (abs >= 1e12) return createDate(asNumber);

    return createDate(asNumber * 1e3);
  }

  if (typeof value !== "string") {
    return null;
  }

  const match =
    /^(\d{4})\/(\d{2})\/(\d{2})(?:@(\d{2}):(\d{2}):(\d{2}))?(Z)?$/.exec(value);

  if (!match) {
    return createDate(new Date(value).getTime());
  }

  const [, y, mo, d, h = "00", mi = "00", s = "00", z] = match;

  return createDate(
    new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}${z ?? ""}`).getTime(),
  );
}

export interface FormatCoveoDateOptions {
  locale: string; // e.g. router.locale
  timeZone?: string; // default: 'UTC' to be SSR-safe
  separator?: string; // default: '|'
}

export function formatCoveoDate(
  raw: number | string | null | undefined,
  { locale, timeZone = "UTC", separator = "|" }: FormatCoveoDateOptions,
): string {
  const date = parseCoveoDate(raw);
  if (!date) return "";

  const dateFmt = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone,
  });

  // Check the hour/minute/second in the target timezone. If all zero, the
  // timestamp is a "date only" value -> skip the time.
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23", // reliably returns "00" for midnight (avoids "24:00" quirks)
    timeZone,
  }).formatToParts(date);

  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "00";

  const isDateOnly =
    get("hour") === "00" && get("minute") === "00" && get("second") === "00";

  if (isDateOnly) return dateFmt.format(date);

  const timeFmt = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });

  return `${dateFmt.format(date)} ${separator} ${timeFmt.format(date)}`;
}

export const getCHImageURLForMetdata = (
  imgArr: Array<ImageArrayObject> | undefined,
): string => {
  if (!imgArr || imgArr.length === 0) return "";
  const chImageEndpoint = process.env.NEXT_PUBLIC_SITECORE_CH_API_URL || "";
  const imgObj = imgArr?.find(
    (item) => item?.resource === "RectangleFullScreen",
  );

  if (!imgObj) return "";
  return `${chImageEndpoint}/api/public/content/${imgObj.relativeUrl}?v=${imgObj.versionHash}`;
};

// Maps a locale tag to its Sitecore Content Hub Localization id.
// Keys are lowercased; the lookup normalizes the input the same way.
const CH_LOCALIZATION_BY_LOCALE: Record<string, string> = {
  "de-ch": "M.Localization.dech",
  "de-at": "M.Localization.deat",
  "en-us": "M.Localization.en-US",
  en: "M.Localization.en-US",
  "de-de": "M.Localization.dede",
  "es-la": "M.Localization.esla",
  "es-mx": "M.Localization.esmx",
  "fr-ca": "M.Localization.frca",
  "pt-br": "M.Localization.ptbr",
  "es-es": "M.Localization.esES",
  "fr-ch": "M.Localization.frch",
  "sv-se": "M.Localization.svse",
  "ru-ru": "M.Localization.ruru",
  "pl-pl": "M.Localization.plpl",
  "no-no": "M.Localization.nono",
  "nl-be": "M.Localization.nlbe",
  "nl-nl": "M.Localization.nlnl",
  "fr-be": "M.Localization.frbe",
  "it-it": "M.Localization.itit",
  "fr-fr": "M.Localization.frfr",
  "ko-kr": "M.Localization.kokr",
  "ja-jp": "M.Localization.jajp",
  "zh-cn": "M.Localization.zhcn",
  "da-dk": "M.Localization.dadk",
  "en-ca": "M.Localization.enca",
  "en-au": "M.Localization.enau",
  "en-at": "M.Localization.enat",
  "en-be": "M.Localization.enbe",
  "en-br": "M.Localization.enbr",
  "en-dk": "M.Localization.endk",
  "en-eg": "M.Localization.eneg",
  "en-fr": "M.Localization.enfr",
  "en-de": "M.Localization.ende",
  "en-in": "M.Localization.enin",
  "en-id": "M.Localization.enid",
  "en-ie": "M.Localization.enie",
  "en-it": "M.Localization.enit",
  "en-jp": "M.Localization.enjp",
  "en-ke": "M.Localization.enke",
  "en-la": "M.Localization.enla",
  "en-my": "M.Localization.enmy",
  "en-mx": "M.Localization.enmx",
  "en-nl": "M.Localization.ennl",
  "en-nz": "M.Localization.ennz",
  "en-no": "M.Localization.enno",
  "en-ph": "M.Localization.enph",
  "en-pl": "M.Localization.enpl",
  "en-sa": "M.Localization.ensa",
  "en-sg": "M.Localization.ensg",
  "en-za": "M.Localization.enza",
  "en-kr": "M.Localization.enkr",
  "en-es": "M.Localization.enes",
  "en-se": "M.Localization.ense",
  "en-ch": "M.Localization.ench",
  "en-th": "M.Localization.enth",
  "en-tr": "M.Localization.entr",
  "en-ae": "M.Localization.enae",
  "en-gb": "M.Localization.engb",
  "en-vn": "M.Localization.envn",
};

export const getCHLocalizationQueryParams = (
  locale: string = "en-us",
): string => CH_LOCALIZATION_BY_LOCALE[locale.toLowerCase()] ?? ""; //?? DEFAULT_CH_LOCALIZATION_ID;

export const castToTaxonomyItemValues = (item?: TaxonomyField): string[] => {
  if (!item || item?.results?.length == 0) return [];
  return item.results.map((item) => item.Label[defaultLocale]);
};

export function formatToIsoWithoutTimezone(localTime: string): string {
  const match = new RegExp(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
  ).exec(localTime);

  if (!match) {
    console.log(
      `Invalid date format. Expected "MM/DD/YYYY hh:mm AM/PM", got "${localTime}"`,
    );
    return "";
  }

  const [, mm, dd, yyyy, hhRaw, min, ampmRaw] = match;

  let hour = Number(hhRaw);
  const minute = Number(min);
  const ampm = ampmRaw.toUpperCase();

  if (hour === 12) hour = 0;
  if (ampm === "PM") hour += 12;

  const hourPadded = String(hour).padStart(2, "0");
  const minPadded = String(minute).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}T${hourPadded}:${minPadded}:00Z`;
}

export const isExternalImage = (imageUrl?: string): boolean => {
  return (
    imageUrl?.startsWith("http://") || imageUrl?.startsWith("https://") || false
  );
};

// Helper function to get optimized image props
export const getImageProps = (imageUrl: string, title: string) => {
  const isExternal = isExternalImage(imageUrl);
  return {
    src: imageUrl,
    alt: title,
    width: isExternal ? 296 : undefined, // Set explicit width for external images
    height: isExternal ? 168 : undefined, // Set explicit height for external images
    fill: !isExternal, // Use fill for internal images
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw",
    quality: 85,
    priority: false, // Set to true for above-the-fold images if needed
    placeholder: "blur" as const,
    blurDataURL: generateShimmerBlur(296, 168),
  };
};

export type CardsCarouselRequestParams = {
  locale: string;
  pageSize: number;
  isDateOnly?: boolean;
  linkLabel?: string;
};

const addTemplateFields = (
  selectedTemplates: string,
  fieldsToInclude: string[],
): void => {
  if (selectedTemplates.includes("NewsPage")) {
    fieldsToInclude.push("taxonomy_news_types", "taxonomy_news_topics");
  }

  if (selectedTemplates.includes("ArticlePage")) {
    fieldsToInclude.push(
      "article_author",
      "taxonomy_article_topics",
      "taxonomy_article_types",
    );
  }

  if (selectedTemplates.includes("EventPage")) {
    fieldsToInclude.push(
      "event_title",
      "event_subtitle",
      "event_description",
      "event_image",
      "event_location",
      "event_start",
      "event_end",
      "event_timezone",
      "event_type",
      "taxonomy_news_topics",
      "taxonomy_news_types",
    );
  }

  if (selectedTemplates.includes("StoryPage")) {
    fieldsToInclude.push("taxonomy_story_types", "taxonomy_story_topics");
  }

  if (selectedTemplates.includes("LocationPage")) {
    fieldsToInclude.push(
      "taxonomy_location_types",
      "taxonomy_location_regions",
    );
  }

  if (selectedTemplates.includes("SolutionPage")) {
    fieldsToInclude.push("taxonomy_solutions");
  }

  if (selectedTemplates.includes("Offering")) {
    fieldsToInclude.push("taxonomy_offering_type");
  }
};

export const fetchCardsCarouselItems = async (
  options: CoveoStaticFetchParams & CoveoConfigOptions,
  params: CardsCarouselRequestParams,
  taxonomyContext?: TaxonomyFields,
  pageId?: string,
): Promise<CoveoCard[]> => {
  const fieldsToInclude = [
    "page_og_title",
    "page_og_description",
    "page_og_image",
    "page_publication_date",
    "page_publication_day",
    "page_publication_month",
    "page_publication_year",
    "page_template_name",
  ];

  const {
    Applications = [],
    Solutions = [],
    Industries = [],
    Markets = [],
    TemplateTypes = [],
    BlogArticleType = [],
    BlogArticleTopic = [],
    BlogArticleAuthor = [],
    NewsLetterName = [],
    NewsType = [],
    NewsTopic = [],
    StoryType = [],
    StoryTopic = [],
    EventsType = [],
    LocationType = [],
    LocationRegion = [],
    Companies = [],
    OfferingType = [],
    TranslationOwner = [],
    GoogleAnalyticsRepGroup = [],
    Countries = [],
    Regions = [],
  } = options || {};

  let cq: string | undefined = undefined;
  let aq: string | undefined = undefined;
  const queryParts: string[] = [];

  const selectedTemplates = TemplateTypes.map(
    (template) => `"${template?.fields?.Value?.value}"`,
  ).join(", ");
  if (selectedTemplates) {
    queryParts.push(
      `${CoveoFieldName.PageTemplateName}==(${selectedTemplates})`,
    );

    addTemplateFields(selectedTemplates, fieldsToInclude);
  }

  const taxonomyClauses: Array<string | undefined> = [
    buildInClause(Markets, CoveoFieldName.TaxonomyMarkets, taxonomyLabelValue),
    buildInClause(
      Applications,
      CoveoFieldName.TaxonomyApplications,
      taxonomyLabelValue,
    ),
    buildInClause(
      Solutions,
      CoveoFieldName.TaxonomySolutions,
      taxonomyLabelValue,
    ),
    buildInClause(
      Industries,
      CoveoFieldName.TaxonomyIndustries,
      taxonomyLabelValue,
    ),
    buildInClause(
      BlogArticleType,
      CoveoFieldName.TaxonomyArticleTypes,
      taxonomyLabelValue,
    ),
    buildInClause(
      BlogArticleTopic,
      CoveoFieldName.TaxonomyArticleTopics,
      taxonomyLabelValue,
    ),
    buildInClause(
      BlogArticleAuthor,
      CoveoFieldName.ArticleAuthor,
      taxonomyLabelValue,
    ),
    buildInClause(
      NewsLetterName,
      CoveoFieldName.TaxonomyNewsLetterNames,
      taxonomyLabelValue,
    ),
    buildInClause(
      NewsType,
      CoveoFieldName.TaxonomyNewsTypes,
      taxonomyLabelValue,
    ),
    buildInClause(
      NewsTopic,
      CoveoFieldName.TaxonomyNewsTopics,
      taxonomyLabelValue,
    ),
    buildInClause(
      StoryType,
      CoveoFieldName.TaxonomyStoryTypes,
      taxonomyLabelValue,
    ),
    buildInClause(
      StoryTopic,
      CoveoFieldName.TaxonomyStoryTopics,
      taxonomyLabelValue,
    ),
    buildInClause(EventsType, CoveoFieldName.EventType, enumOptionValue),
    buildInClause(
      LocationType,
      CoveoFieldName.TaxonomyLocationTypes,
      taxonomyLabelValue,
    ),
    buildInClause(
      LocationRegion,
      CoveoFieldName.TaxonomyLocationRegions,
      taxonomyLabelValue,
    ),
    buildInClause(
      Companies,
      CoveoFieldName.TaxonomyCompanies,
      taxonomyLabelValue,
    ),
    buildInClause(
      OfferingType,
      CoveoFieldName.TaxonomyOfferingType,
      taxonomyLabelValue,
    ),
    buildInClause(
      TranslationOwner,
      CoveoFieldName.TaxonomyTranslationOwner,
      taxonomyLabelValue,
    ),
    buildInClause(
      Countries,
      CoveoFieldName.TaxonomyCountries,
      taxonomyLabelValue,
    ),
    buildInClause(Regions, CoveoFieldName.TaxonomyRegions, taxonomyLabelValue),
    buildInClause(
      GoogleAnalyticsRepGroup,
      CoveoFieldName.TaxonomyGoogleAnalyticsRepGroup,
      taxonomyLabelValue,
    ),
  ];

  for (const clause of taxonomyClauses) {
    if (clause) queryParts.push(clause);
  }

  if (params?.locale) {
    queryParts.push(`${CoveoFieldName.PageLang}=="${params.locale}"`);
  }

  if (pageId) {
    queryParts.push(`NOT ${CoveoFieldName.PageId}=="${pageId}"`);
  }

  if (queryParts.length > 0) {
    cq = queryParts.join(" AND ");
  }

  const sortField = options?.SortField?.value;
  const sortDirection = options?.SoftFieldDirection?.value;
  const sortCriteria =
    sortField && sortDirection
      ? `@${sortField} ${sortDirection.toLowerCase()}`
      : `@page_publication_date descending`;

  const capitalizedRegionLocale = capitalizeLanguageRegion(params.locale);

  if (
    taxonomyContext &&
    options.ContextTaxonomies &&
    options.ContextTaxonomies.length > 0
  ) {
    aq = buildTaxonomyContextQuery(
      taxonomyContext,
      options.ContextTaxonomies,
      capitalizedRegionLocale,
    );
  }

  const query: CoveoSearchApiQuery = {
    locale: capitalizedRegionLocale || "en-US",
    searchHub: process.env.NEXT_PUBLIC_COVEO_SEARCH_HUB,
    numberOfResults: params.pageSize,
    sortCriteria: sortCriteria,
    pipeline: process.env.NEXT_PUBLIC_COVEO_PIPELINE,
    cq: cq,
    aq: aq,
    fieldsToInclude: fieldsToInclude,
  };

  const response = await CoveoSearchRequest(query);

  const cards: CoveoCard[] = response.results.map((result) => {
    const title =
      result.raw["event_title"] || result.raw["page_og_title"] || "";
    const description =
      result.raw["event_description"] ||
      result.raw["page_og_description"] ||
      result.excerpt;
    const image =
      result.raw["event_image"] ||
      result.raw["page_og_image"] ||
      fallbackImage.src;
    let date: string | null = null;

    if (result.raw["page_template_name"] === "EventPage") {
      const dateRaw =
        result.raw["event_start"] || result.raw["page_publication_date"];
      date =
        formatCoveoDateWithoutConversion(dateRaw, params.isDateOnly) || null;
    } else if (
      result.raw["page_template_name"] === "NewsPage" ||
      result.raw["page_template_name"] === "StoryPage" ||
      result.raw["page_template_name"] === "ArticlePage"
    ) {
      const dateRaw = result.raw["page_publication_date"];
      date =
        formatCoveoDate(dateRaw, {
          locale: params.locale,
        }) || null;
    }
    return {
      title: title,
      desc: description,
      image: image,
      link: result.clickUri,
      linkLabel: params.linkLabel || "Show More",
      date: date || null,
      subtitle: result.raw["event_subtitle"] || null,
      timezone: result.raw["event_timezone"] || null,
    };
  });
  return cards;
};

export const getImageURLForCHPages = (
  imgArr: Array<ImageArrayObject> | undefined,
) => {
  if (imgArr && imgArr?.length === 0) return "";

  const imgObj =
    imgArr?.find((item) => item?.resource === "downloadOriginal") ??
    imgArr?.find(
      (item) =>
        item?.resource === "downloadPreview" || item?.resource === "landscape",
    );

  if (!imgObj) return "";

  const chImageEndpoint = process.env.NEXT_PUBLIC_SITECORE_CH_API_URL || "";
  return `${chImageEndpoint}/api/public/content/${imgObj?.relativeUrl}?v=${imgObj?.versionHash}`;
};
